import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Map, Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../MobileMap.css';

const MobileTaxiMap = ({ driver, tripDetails }) => {
  const mapRef = useRef(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  
  // Find pickup and dropoff locations
  const pickupLocation = tripDetails.find(location => location.locationType === 'pickup');
  const dropoffLocation = tripDetails.find(location => location.locationType === 'dropoff');
  
  // Extract rider information if available
  const riderInfo = useMemo(() => driver?.rider_info || {}, [driver]);
  const hasRiderLocation = useMemo(() => {
    return riderInfo.latitude !== null && 
           riderInfo.latitude !== undefined && 
           riderInfo.longitude !== null && 
           riderInfo.longitude !== undefined;
  }, [riderInfo]);
  
  // Calculate center for initial view
  const calculateCenter = useMemo(() => {
    // Default to center of the map if no coordinates available
    let centerCoords = [0, 0];
    
    // Prioritize rider location if available
    if (hasRiderLocation) {
      centerCoords = [riderInfo.longitude, riderInfo.latitude];
    }
    // Then try driver location
    else if (driver && driver.latitude != null && driver.longitude != null) {
      centerCoords = [driver.longitude, driver.latitude];
    }
    // Then try pickup/dropoff midpoint
    else if (pickupLocation && dropoffLocation) {
      const centerLng = (pickupLocation.longitude + dropoffLocation.longitude) / 2;
      const centerLat = (pickupLocation.latitude + dropoffLocation.latitude) / 2;
      centerCoords = [centerLng, centerLat];
    }
    
    return centerCoords;
  }, [hasRiderLocation, riderInfo, driver, pickupLocation, dropoffLocation]);
  
  // Handle map loading
  useEffect(() => {
    if (mapRef.current && !mapInitialized) {
      setMapInitialized(true);
    }
  }, [mapRef, mapInitialized]);
  
  // Handle map focus updates
  useEffect(() => {
    if (!mapRef.current || !mapInitialized) return;
    
    const map = mapRef.current.getMap();
    
    // Focus priority: rider location > pickup/dropoff bounds
    if (hasRiderLocation) {
      console.log('Focusing on rider location:', riderInfo.latitude, riderInfo.longitude);
      map.flyTo({
        center: [riderInfo.longitude, riderInfo.latitude],
        zoom: 15,
        essential: true,
        duration: 1000
      });
    } else if (pickupLocation && dropoffLocation) {
      console.log('Fitting bounds to pickup/dropoff');
      map.fitBounds(
        [
          [pickupLocation.longitude, pickupLocation.latitude],
          [dropoffLocation.longitude, dropoffLocation.latitude]
        ],
        { padding: 40, maxZoom: 13 }
      );
    }
  }, [mapInitialized, hasRiderLocation, riderInfo, pickupLocation, dropoffLocation]);
  
  return (
    <div className="mobile-map-container">
      <Map
        ref={mapRef}
        initialViewState={{
          latitude: calculateCenter[1],
          longitude: calculateCenter[0],
          zoom: 12
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={"mapbox://styles/mapbox/streets-v12"}
        mapboxAccessToken="pk.eyJ1IjoicmVrYXIiLCJhIjoiY2swYjI1czZzMG5xczNubXd4ODZ5ZmdvNyJ9.UUIH_qL1wzfGoFX2eG6Cyg"
        attributionControl={false}
      >
        {/* Markers */}
        {pickupLocation && (
          <Marker 
            latitude={pickupLocation.latitude}
            longitude={pickupLocation.longitude}
            anchor="center"
          >
            <div 
              className="mobile-custom-marker mobile-pickup-marker"
              onTouchStart={() => setHoveredMarker('pickup')}
              onTouchEnd={() => setTimeout(() => setHoveredMarker(null), 1500)}
            ></div>
          </Marker>
        )}
        
        {dropoffLocation && (
          <Marker 
            latitude={dropoffLocation.latitude}
            longitude={dropoffLocation.longitude}
            anchor="center"
          >
            <div 
              className="mobile-custom-marker mobile-dropoff-marker"
              onTouchStart={() => setHoveredMarker('dropoff')}
              onTouchEnd={() => setTimeout(() => setHoveredMarker(null), 1500)}
            ></div>
          </Marker>
        )}

        {/* Rider location marker if available */}
        {hasRiderLocation && (
          <Marker 
            latitude={riderInfo.latitude}
            longitude={riderInfo.longitude}
            anchor="center"
          >
            <div 
              className="mobile-custom-marker mobile-rider-marker"
              onTouchStart={() => setHoveredMarker('rider')}
              onTouchEnd={() => setTimeout(() => setHoveredMarker(null), 1500)}
            >
              <i className="fas fa-user"></i>
            </div>
          </Marker>
        )}

        {/* Driver location marker (taxi icon) */}
        {driver && driver.latitude != null && driver.longitude != null && (
          <Marker 
            latitude={driver.latitude}
            longitude={driver.longitude}
            anchor="center"
          >
            <div 
              className="mobile-custom-marker mobile-driver-marker"
              onTouchStart={() => setHoveredMarker('driver')}
              onTouchEnd={() => setTimeout(() => setHoveredMarker(null), 1500)}
            >
              <i className="fas fa-taxi"></i>
            </div>
          </Marker>
        )}

        {/* Popups for mobile */}
        {hoveredMarker === 'pickup' && pickupLocation && (
          <Popup
            latitude={pickupLocation.latitude}
            longitude={pickupLocation.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
            className="mobile-marker-popup"
          >
            <div className="mobile-popup-content pickup">PICKUP</div>
          </Popup>
        )}
        
        {hoveredMarker === 'dropoff' && dropoffLocation && (
          <Popup
            latitude={dropoffLocation.latitude}
            longitude={dropoffLocation.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
            className="mobile-marker-popup"
          >
            <div className="mobile-popup-content dropoff">DROPOFF</div>
          </Popup>
        )}

        {hoveredMarker === 'rider' && hasRiderLocation && (
          <Popup
            latitude={riderInfo.latitude}
            longitude={riderInfo.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
            className="mobile-marker-popup"
          >
            <div className="mobile-popup-content rider">YOU</div>
          </Popup>
        )}

        {hoveredMarker === 'driver' && driver && driver.latitude && driver.longitude && (
          <Popup
            latitude={driver.latitude}
            longitude={driver.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            offset={15}
            className="mobile-marker-popup"
          >
            <div className="mobile-popup-content driver">DRIVER</div>
          </Popup>
        )}
      </Map>
    </div>
  );
};

export default MobileTaxiMap;
